import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { companyInfo } from '../data';

const Contact = () => {
  const [ref, isVisible] = useScrollAnimation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  // WhatsApp number (hardcoded as requested)
  const whatsappNumber = '966535221070';

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Format the message for WhatsApp
    const whatsappMessage = `
*New Inquiry from Al Abeer Diamond Website*

*Name:* ${formData.name}
*Email:* ${formData.email}
*Phone:* ${formData.phone || 'Not provided'}
*Subject:* ${formData.subject || 'Not provided'}

*Message:*
${formData.message}

---
*Sent from:* ${window.location.href}
    `.trim();

    // Create WhatsApp URL with the specific number
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(whatsappMessage)}`;

    // Open WhatsApp in a new window/tab
    window.open(whatsappUrl, '_blank');

    // Show success message
    setSubmitStatus('success');
    setIsSubmitting(false);
    
    // Reset form after a delay
    setTimeout(() => {
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      setTimeout(() => setSubmitStatus(null), 3000);
    }, 2000);
  };

  return (
    <section className="section-padding bg-gray-50" ref={ref}>
      <div className="container-custom">
        <div className="text-center mb-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <span className="section-badge">Get In Touch</span>
            <h2 className="heading-primary mt-4">
              Contact <span className="text-gradient">Us</span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto mt-4">
              Ready to start your project? Contact us today for a consultation.
            </p>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isVisible ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="lg:col-span-1"
          >
            <div className="bg-white rounded-2xl p-6 shadow-soft">
              <h3 className="text-xl font-bold text-secondary font-oswald mb-6">
                Contact Information
              </h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="uil uil-location-pin-alt text-primary"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-secondary">Address</p>
                    <p className="text-sm text-gray-600">{companyInfo.address}</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="uil uil-phone-volume text-primary"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-secondary">Phone</p>
                    <p className="text-sm text-gray-600">
                      <a href={`tel:${companyInfo.phone1.replace(/\s/g, '')}`} className="hover:text-primary transition-colors">
                        {companyInfo.phone1}
                      </a>
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="uil uil-envelope text-primary"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-secondary">Email</p>
                    <a href={`mailto:${companyInfo.email}`} className="text-sm text-gray-600 hover:text-primary transition-colors">
                      {companyInfo.email}
                    </a>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="uil uil-globe text-primary"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-secondary">Website</p>
                    <a href={`https://${companyInfo.website}`} target="_blank" rel="noopener noreferrer" className="text-sm text-gray-600 hover:text-primary transition-colors">
                      {companyInfo.website}
                    </a>
                  </div>
                </div>
              </div>

              {/* Quick WhatsApp Button */}
              <div className="mt-6 pt-6 border-t border-gray-100">
                <a
                  href={`https://wa.me/${whatsappNumber}?text=${encodeURIComponent('Hello Al Abeer Diamond, I would like to inquire about your services.')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center space-x-2 bg-green-500 hover:bg-green-600 text-white px-4 py-3 rounded-lg transition-all duration-300 w-full"
                >
                  <i className="uil uil-whatsapp text-xl"></i>
                  <span className="font-medium">Chat on WhatsApp</span>
                </a>
              </div>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isVisible ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:col-span-2"
          >
            <div className="bg-white rounded-2xl p-6 shadow-soft">
              <h3 className="text-xl font-bold text-secondary font-oswald mb-2">
                Send Us a Message
              </h3>
              <p className="text-gray-500 text-sm mb-6">
                Fill in the form below and we'll redirect you to WhatsApp with your inquiry details.
              </p>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-secondary mb-1">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition"
                      placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary mb-1">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-secondary mb-1">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition"
                      placeholder="+966 5X XXX XXXX"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary mb-1">
                      Subject
                    </label>
                    <input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition"
                      placeholder="Project inquiry"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary mb-1">
                    Message *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="5"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition resize-none"
                    placeholder="Tell us about your project..."
                  ></textarea>
                </div>

                <div className="flex items-center justify-between flex-wrap gap-4">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-primary flex items-center justify-center"
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Processing...
                      </>
                    ) : (
                      <>
                        <i className="uil uil-whatsapp text-xl mr-2"></i>
                        Send via WhatsApp
                      </>
                    )}
                  </button>

                  <p className="text-xs text-gray-400">
                    <i className="uil uil-lock-alt mr-1"></i>
                    Your data will be sent securely via WhatsApp
                  </p>
                </div>

                {submitStatus === 'success' && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-4 bg-green-50 text-green-700 rounded-lg border border-green-200"
                  >
                    <i className="uil uil-check-circle mr-2"></i>
                    Opening WhatsApp... Please send the message to complete your inquiry.
                  </motion.div>
                )}
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;